import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Drawer, Input, Button, List, Typography, Space, Spin, message, Card, Badge, Segmented } from 'antd';
import { SendOutlined, RobotOutlined, UserOutlined, AudioOutlined, StopOutlined, CheckCircleOutlined, ExclamationCircleOutlined, FileWordOutlined } from '@ant-design/icons';
import { Document, Packer, Paragraph as DocxParagraph, TextRun, HeadingLevel } from 'docx';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../utils/config';
import chatbotImage from '../../assets/chatbot.svg';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const SYSTEM_PROMPT_SNIPPET =
  'Sen yardımcı bir asistansın. Kullanıcının form doldurmasına yardımcı ol.';

const Chatbot = ({ sqlKey = null }) => {
  const { darkMode } = useTheme();
  const { getAuthHeaders, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [currentFormId, setCurrentFormId] = useState(null);
  const [sendMode, setSendMode] = useState('islem'); // 'islem' | 'sor'
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const storageKey = `chatbot_messages_${sqlKey || 'general'}`;

  const textColor = (isUser) =>
    isUser ? '#ffffff' : (darkMode ? '#ffffff' : '#000000');

  const renderMessageContent = (content, isAssistant = false) => {
    if (typeof content !== 'string') return content;
    const color = textColor(!isAssistant);

    if (isAssistant) {
      return (
        <div className="chat-markdown" style={{ color, wordBreak: 'break-word' }}>
          <ReactMarkdown
            components={{
              h1: ({ node, ...props }) => <h1 style={{ margin: '12px 0 8px', fontSize: '1.25rem', fontWeight: 700, color }} {...props} />,
              h2: ({ node, ...props }) => <h2 style={{ margin: '10px 0 6px', fontSize: '1.1rem', fontWeight: 600, color }} {...props} />,
              h3: ({ node, ...props }) => <h3 style={{ margin: '8px 0 4px', fontSize: '1rem', fontWeight: 600, color }} {...props} />,
              p: ({ node, ...props }) => <p style={{ margin: '6px 0', lineHeight: 1.5, color }} {...props} />,
              ul: ({ node, ...props }) => <ul style={{ margin: '6px 0', paddingLeft: 20, color }} {...props} />,
              ol: ({ node, ...props }) => <ol style={{ margin: '6px 0', paddingLeft: 20, color }} {...props} />,
              li: ({ node, ...props }) => <li style={{ marginBottom: 4, color }} {...props} />,
              strong: ({ node, ...props }) => <strong style={{ color, fontWeight: 700 }} {...props} />,
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      );
    }

    const segments = content.split(/\*\*(.*?)\*\*/g);
    if (segments.length === 1) {
      return content.split('\n').map((line, idx) => (
        <React.Fragment key={idx}>
          {line}
          {idx < content.split('\n').length - 1 && <br />}
        </React.Fragment>
      ));
    }
    return segments.map((segment, index) => {
      const isBold = index % 2 === 1;
      const lines = segment.split('\n');
      return (
        <React.Fragment key={index}>
          {lines.map((line, idx) => (
            <React.Fragment key={idx}>
              {isBold ? <strong>{line}</strong> : line}
              {idx < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </React.Fragment>
      );
    });
  };

  const normalizeMessage = (msg) => {
    if (!msg) return null;
    if (msg._normalized) return msg;
    
    const role = msg.role || (msg.type === 'user' ? 'user' : 'assistant');
    const type = role === 'user' ? 'user' : 'bot';
    const content = msg.content ?? msg.text ?? '';
    
    if (role === 'system') {
      return null;
    }
    if (typeof content === 'string' && content.includes(SYSTEM_PROMPT_SNIPPET)) {
      return null;
    }

    const timestamp = msg.timestamp || new Date().toISOString();
    
    return {
      type,
      role,
      content,
      timestamp,
      isError: msg.isError || false,
      metadata: msg.metadata || {},
      _normalized: true
    };
  };

  const textToDocxRuns = (text) => {
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, i) => new TextRun({ text: part, bold: i % 2 === 1 }));
  };

  const downloadAsWord = async (content) => {
    if (typeof content !== 'string' || !content.trim()) {
      message.warning('İndirilecek içerik yok');
      return;
    }
    const lines = content.split(/\r?\n/);
    const children = [];
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        children.push(new DocxParagraph({ text: '', spacing: { after: 80 } }));
        continue;
      }
      if (/^###\s+/.test(trimmed)) {
        children.push(new DocxParagraph({
          text: trimmed.replace(/^###\s+/, ''),
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 240, after: 120 }
        }));
      } else if (/^##\s+/.test(trimmed)) {
        children.push(new DocxParagraph({
          text: trimmed.replace(/^##\s+/, ''),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 280, after: 120 }
        }));
      } else if (/^#\s+/.test(trimmed)) {
        children.push(new DocxParagraph({
          text: trimmed.replace(/^#\s+/, ''),
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 320, after: 120 }
        }));
      } else if (/^[\*\-]\s+/.test(trimmed)) {
        const bulletText = trimmed.replace(/^[\*\-]\s+/, '');
        children.push(new DocxParagraph({
          children: textToDocxRuns(`• ${bulletText}`),
          spacing: { after: 80 }
        }));
      } else {
        children.push(new DocxParagraph({
          children: textToDocxRuns(trimmed),
          spacing: { after: 80 }
        }));
      }
    }
    try {
      const doc = new Document({
        sections: [{ children }]
      });
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `asistan-${new Date().toISOString().slice(0, 10)}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('Word dosyası indirildi');
    } catch (e) {
      console.error(e);
      message.error('Word dosyası oluşturulurken hata oluştu');
    }
  };

  useEffect(() => {
    try {
      const savedMessages = sessionStorage.getItem(storageKey);
      if (savedMessages) {
        const parsed = JSON.parse(savedMessages);
        const normalized = parsed.map(normalizeMessage).filter(Boolean);
        setMessages(normalized);
        
        for (let i = normalized.length - 1; i >= 0; i--) {
          const msg = normalized[i];
          if (msg.metadata?.form_id) {
            setCurrentFormId(msg.metadata.form_id);
            console.log('[CHATBOT] Form ID sessionStorage\'den yüklendi:', msg.metadata.form_id);
            break;
          }
        }
      }
    } catch (error) {
      console.error('Mesajlar sessionStorage\'den yüklenirken hata:', error);
    }
  }, [storageKey]);

  useEffect(() => {
    if (messages.length > 0) {
      try {
        const toSave = messages.map((m) => ({
          type: m.type,
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
          isError: m.isError,
          metadata: m.metadata
        }));
        sessionStorage.setItem(storageKey, JSON.stringify(toSave));
      } catch (e) {
        console.error('Mesajlar kaydedilirken hata:', e);
      }
    }
  }, [messages, storageKey]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (open) {
      scrollToBottom();
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [open, messages]);

  const handleSend = async () => {
    if (!question.trim()) return;
    
    const userMessage = normalizeMessage({
      role: 'user',
      content: question.trim(),
      timestamp: new Date().toISOString()
    });
    
    setMessages(prev => [...prev, userMessage]);
    setQuestion('');
    setLoading(true);

    try {
      const conversationHistory = messages.map((m) => ({
        role: m.role,
        content: m.content
      }));
      
      const payload = {
        user_input: userMessage.content,
        conversation_history: conversationHistory,
        form_id: currentFormId || 0,
        user_id: user?.kullanici_no || user?.id || 1
      };

      console.log('[CHATBOT] Payload gönderiliyor:', {
        ...payload,
        conversation_history: `${payload.conversation_history.length} mesaj`
      });

      const endpoint = sendMode === 'sor' ? '/chatbot/sor' : '/chatbot/chat';
      const response = await axios.post(
        `${API_BASE_URL}${endpoint}`,
        payload,
        { headers: getAuthHeaders() }
      );

      console.log('[CHATBOT] Response alındı:', response.data);

      if (response.data?.updated_history?.length) {
        const normalized = response.data.updated_history.map(normalizeMessage).filter(Boolean);
        setMessages(normalized);
        
        for (let i = normalized.length - 1; i >= 0; i--) {
          if (normalized[i].metadata?.form_id) {
            setCurrentFormId(normalized[i].metadata.form_id);
            console.log('[CHATBOT] Form ID güncellendi:', normalized[i].metadata.form_id);
            break;
          }
        }
      } else {
        const botMessage = normalizeMessage({
          role: 'assistant',
          content: response.data.reply || 'Cevap alınamadı',
          timestamp: new Date().toISOString(),
          metadata: response.data.metadata || {}
        });
        
        setMessages(prev => [...prev, botMessage]);
        
        if (botMessage.metadata?.form_id) {
          setCurrentFormId(botMessage.metadata.form_id);
          console.log('[CHATBOT] Form ID güncellendi:', botMessage.metadata.form_id);
        }
      }

      if (response.data?.tool_calls?.length) {
        const successCalls = response.data.tool_calls.filter((t) => t.success);
        const failedCalls = response.data.tool_calls.filter((t) => !t.success);
        
        if (successCalls.length > 0) {
          const formNames = successCalls
            .map((t) => t.form_name || `Form #${t.form_id}`)
            .join(', ');
          message.success(`✓ ${formNames} kaydedildi`);
        }
        
        if (failedCalls.length > 0) {
          failedCalls.forEach((t) => {
            message.error(`✗ ${t.form_name || `Form #${t.form_id}`}: ${t.message}`);
          });
        }
      }
    } catch (error) {
      console.error('[CHATBOT] Hata:', error);
      const errorMessage = normalizeMessage({
        role: 'assistant',
        content: error.response?.data?.detail || error.message || 'Bir hata oluştu',
        timestamp: new Date().toISOString(),
        isError: true
      });
      setMessages(prev => [...prev, errorMessage]);
      message.error('Mesaj gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        await handleVoiceCommand(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setRecording(true);
      setAudioChunks(chunks);
      message.info('Ses kaydı başladı');
    } catch (error) {
      console.error('Mikrofon erişim hatası:', error);
      message.error('Mikrofon erişimi sağlanamadı');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && recording) {
      mediaRecorder.stop();
      setRecording(false);
      setMediaRecorder(null);
      message.info('Ses kaydı durduruldu');
    }
  };

  const handleVoiceCommand = async (audioBlob) => {
    setLoading(true);
    
    const userMessage = normalizeMessage({
      role: 'user',
      content: 'Ses komutu gönderiliyor...',
      timestamp: new Date().toISOString()
    });
    
    setMessages(prev => [...prev, userMessage]);
    
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice-command.webm');

      const response = await axios.post(
        `${API_BASE_URL}/ask/voice-command`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setMessages(prev => {
          const updated = [...prev];
          const lastIndex = updated.length - 1;
          if (lastIndex >= 0 && updated[lastIndex]?.role === 'user') {
            updated[lastIndex] = normalizeMessage({
              ...updated[lastIndex],
              content: `🎤 ${response.data.transcribed_text || 'Ses komutu'}`
            });
          }
          return updated;
        });

        const botMessage = normalizeMessage({
          role: 'assistant',
          content: response.data.message || 'İşlem tamamlandı',
          timestamp: new Date().toISOString(),
          metadata: response.data.metadata || {}
        });
        
        setMessages(prev => [...prev, botMessage]);

        if (response.data.tool_calls && response.data.tool_calls.length > 0) {
          const successCalls = response.data.tool_calls.filter(tc => tc.success);
          if (successCalls.length > 0) {
            const formNames = successCalls.map(tc => tc.form_name).join(', ');
            message.success(`${formNames} kaydedildi`);
          }
        }
      } else {
        const errorMessage = normalizeMessage({
          role: 'assistant',
          content: response.data.error || 'Ses komutu işlenemedi',
          timestamp: new Date().toISOString(),
          isError: true
        });
        setMessages(prev => [...prev, errorMessage]);
        message.error(response.data.error || 'Ses komutu işlenemedi');
      }
    } catch (error) {
      console.error('Voice command error:', error);
      const errorMessage = normalizeMessage({
        role: 'assistant',
        content: error.response?.data?.error || 'Ses komutu gönderilirken hata oluştu',
        timestamp: new Date().toISOString(),
        isError: true
      });
      setMessages(prev => [...prev, errorMessage]);
      message.error('Ses komutu gönderilirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setCurrentFormId(null);
    try {
      sessionStorage.removeItem(storageKey);
      message.success('Sohbet geçmişi temizlendi');
    } catch (error) {
      console.error('sessionStorage temizlenirken hata:', error);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <Button
        shape="circle"
        size="large"
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '60px',
          height: '60px',
          zIndex: 1000,
          border: 'none',
          backgroundColor: 'transparent',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'none'
        }}
      >
        <img
          src={chatbotImage}
          alt="Chatbot"
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            objectFit: 'contain',
            pointerEvents: 'none'
          }}
        />
      </Button>

      {/* Chatbot Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Space>
              <RobotOutlined style={{ color: '#667eea' }} />
              <Text strong>Asistan</Text>
            </Space>
          </div>
        }
        placement="right"
        onClose={() => setOpen(false)}
        open={open}
        width={window.innerWidth < 768 ? '100%' : 500}
        bodyStyle={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: darkMode ? '#141414' : '#f5f5f5'
        }}
        headerStyle={{
          backgroundColor: darkMode ? '#1f1f1f' : '#ffffff',
          borderBottom: `1px solid ${darkMode ? '#303030' : '#f0f0f0'}`
        }}
        extra={
          messages.length > 0 && (
            <Button
              type="text"
              size="small"
              onClick={clearChat}
              style={{ color: darkMode ? '#ffffff' : '#000000' }}
            >
              Temizle
            </Button>
          )
        }
      >
        {/* Messages Area */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}
        >
          {messages.length === 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
                padding: '20px',
                color: darkMode ? '#8c8c8c' : '#595959'
              }}
            >
              <RobotOutlined style={{ fontSize: '48px', marginBottom: '16px', color: '#667eea' }} />
              <Text style={{ fontSize: '16px', marginBottom: '8px' }}>Merhaba! Size nasıl yardımcı olabilirim?</Text>
            </div>
          )}

          {messages.map((msg, index) => {
            if (!msg) return null;
            
            const hasToolCalls = msg.metadata?.tool_calls?.length > 0;
            
            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: '8px'
                }}
              >
                <Card
                  style={{
                    maxWidth: '80%',
                    backgroundColor: msg.type === 'user'
                      ? (darkMode ? '#1890ff' : '#1890ff')
                      : (darkMode ? '#1f1f1f' : '#ffffff'),
                    border: msg.isError
                      ? `1px solid ${darkMode ? '#ff4d4f' : '#ff4d4f'}`
                      : `1px solid ${darkMode ? '#303030' : '#f0f0f0'}`,
                    borderRadius: '12px'
                  }}
                  bodyStyle={{
                    padding: '12px 16px'
                  }}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space size="small" style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space size="small">
                        {msg.type === 'user' ? (
                          <UserOutlined style={{ color: '#ffffff' }} />
                        ) : (
                          <RobotOutlined style={{ color: msg.isError ? '#ff4d4f' : '#667eea' }} />
                        )}
                        <Text
                          strong
                          style={{
                            color: msg.type === 'user' ? '#ffffff' : (darkMode ? '#ffffff' : '#000000'),
                            fontSize: '12px'
                          }}
                        >
                          {msg.type === 'user' ? 'Siz' : 'Asistan'}
                        </Text>
                      </Space>
                      {msg.type === 'bot' && msg.content && (
                        <Button
                          type="text"
                          size="small"
                          icon={<FileWordOutlined />}
                          onClick={() => downloadAsWord(msg.content)}
                          style={{
                            color: darkMode ? '#8c8c8c' : '#595959',
                            fontSize: '12px',
                            padding: '0 4px'
                          }}
                          title="Word olarak indir"
                        >
                          Word
                        </Button>
                      )}
                    </Space>
                    <Paragraph
                      style={{
                        margin: 0,
                        color: msg.type === 'user' ? '#ffffff' : (darkMode ? '#ffffff' : '#000000'),
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {renderMessageContent(msg.content, msg.type === 'bot')}
                    </Paragraph>
                    
                    {/* Tool call sonuçları */}
                    {hasToolCalls && (
                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${darkMode ? '#434343' : '#f0f0f0'}` }}>
                        {msg.metadata.tool_calls.map((tc, tcIndex) => (
                          <div key={tcIndex} style={{ marginBottom: '4px' }}>
                            <Space size="small">
                              {tc.success ? (
                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                              ) : (
                                <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                              )}
                              <Text 
                                style={{ 
                                  fontSize: '11px', 
                                  color: darkMode ? '#8c8c8c' : '#595959' 
                                }}
                              >
                                {tc.form_name || `Form #${tc.form_id}`}: {tc.message}
                              </Text>
                            </Space>
                          </div>
                        ))}
                      </div>
                    )}
                  </Space>
                </Card>
              </div>
            );
          })}

          {loading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginBottom: '8px'
              }}
            >
              <Card
                style={{
                  backgroundColor: darkMode ? '#1f1f1f' : '#ffffff',
                  border: `1px solid ${darkMode ? '#303030' : '#f0f0f0'}`,
                  borderRadius: '12px'
                }}
                bodyStyle={{ padding: '12px 16px' }}
              >
                <Space>
                  <RobotOutlined style={{ color: '#667eea' }} />
                  <Spin size="small" />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Düşünüyorum...
                  </Text>
                </Space>
              </Card>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div
          style={{
            padding: '16px',
            borderTop: `1px solid ${darkMode ? '#303030' : '#f0f0f0'}`,
            backgroundColor: darkMode ? '#1f1f1f' : '#ffffff'
          }}
        >
          <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'flex-end' }}>
            <Segmented
              value={sendMode}
              onChange={setSendMode}
              options={[
                { value: 'islem', label: 'İşlem Yap' },
                { value: 'sor', label: 'Sor' }
              ]}
              disabled={loading || recording}
              size="small"
              style={{
                backgroundColor: darkMode ? '#0f0f0f' : '#f0f0f0',
                padding: 2,
                borderRadius: 8
              }}
            />
          </div>
          <Space.Compact style={{ width: '100%' }}>
            <Button
              type={recording ? 'danger' : 'default'}
              icon={recording ? <StopOutlined /> : <AudioOutlined />}
              onClick={recording ? stopRecording : startRecording}
              disabled={loading}
              style={{
                height: 'auto',
                backgroundColor: recording ? '#ff4d4f' : (darkMode ? '#2a2a2a' : '#f5f5f5'),
                border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
                color: recording ? '#fff' : (darkMode ? '#fff' : '#000')
              }}
            />
            <TextArea
              ref={inputRef}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Sorunuzu yazın veya ses ikonuna tıklayın"
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={loading || recording}
              style={{
                backgroundColor: darkMode ? '#141414' : '#ffffff',
                color: darkMode ? '#ffffff' : '#000000',
                border: `1px solid ${darkMode ? '#303030' : '#d9d9d9'}`
              }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={loading}
              disabled={!question.trim() || loading || recording}
              style={{
                height: 'auto',
                background: 'linear-gradient(135deg, #667eea 50%, #764ba2 100%)',
                border: 'none', 
                color: '#ffffff'
              }}
            >
              Gönder
            </Button>
          </Space.Compact>
        </div>
      </Drawer>
    </>
  );
};

export default Chatbot;